import { createElement, type ReactElement } from 'react';
import {
  Label,
  AlertBanner,
  type PassengerCardProps,
  type PassengerInfo,
  type DynamicFormSection,
  type DynamicFieldConfig,
  type ValidationRule,
} from '@fz/ui-components-react';
import type {
  NotCheckedInPax,
  ApisInfo,
  CountryOption,
  EmergencyContactDetails,
  ValidationRules,
} from '@models/ui/pnr-data-model';
import type { Country, Question, Questionnaire } from '@models/master-data/master-data.model';
import type { TimRequest } from '@models/landing/api/tim.model';
import { PassengerTypeEnum } from '@models/landing/ui/select-passenger-model';
import type { ApisFields } from '@models/landing/api/api-response.model';

import {
  useMasterDataStore,
  selectResourceLists,
  selectCountries,
  selectQuestionnaire,
} from '@store/master-data.store';
import { useAppStore, selectSkywardNumber } from '@store/app.store';
import {
  ApisFieldCode,
  ApisFormFieldKey,
  ApisFormSectionId,
} from '@models/landing/field-codes.constants';
import { getResource } from '@utils/resource-translator.util';
import { toTitleCase } from '@api/landing/retrieve.mapper';
import { getCountryOptionsWithSelected } from '@helper/contact-details.mapper';
import { getPassengerTypeLabel } from '@helper/passenger-table-view.mapper';

/** Matches SelectOptionGroup from @fz/ui-components-react DynamicFormFields */
interface SelectOptionGroup {
  groupLabel: string;
  options: { value: string; label: string }[];
}

// Document type codes used in APIS forms
export const DocumentType = {
  PASSPORT: '1',
  NATIONAL_ID: '23',
} as const;

// Local enum for field types (string values)
export const FieldType = {
  TEXT: 'text',
  SELECT: 'select',
  DATE: 'date',
  CHECKBOX: 'checkbox',
  COUNTRY_CODE: 'country_code',
} as const;

/**
 * Maps NotCheckedInPax array to PassengerCard props array
 */
export const mapPassengersToCards = (
  passengers: NotCheckedInPax[],
  activeIndex: number,
): PassengerCardProps[] => {
  return passengers.map((passenger, index) => ({
    passenger: {
      name: passenger.fullName ?? '',
      type: getResource(`${toTitleCase(passenger.passengerType)}.Text`),
    },
    isSelected: index === activeIndex,
    isFilled: Boolean(passenger.isApisOk),
  }));
};

/**
 * Maps single passenger to PassengerInfo
 */
export const mapPassengerInfo = (passenger: NotCheckedInPax): PassengerInfo => ({
  name: passenger.fullName ?? '',
  type: getPassengerTypeLabel(passenger.passengerType),
});

const toStr = (val: string | undefined): string => val ?? '';

export interface FieldErrorContext {
  passengerIndex?: number;
  fieldKey: string;
  documentType?: string;
  fieldError: unknown;
}

export type FieldErrorHandler = (context: FieldErrorContext) => void;

/**
 * Maps ApisInfo to DynamicFormFields values format
 * Keys match the field keys defined in DynamicFormSection configs
 * Returns Record<string, string> for controlled form state
 */
export const mapApisInfoToDynamicFormValues = (
  apisInfo: ApisInfo | undefined,
): Record<string, string> => ({
  // Basic Information
  nationality: toStr(apisInfo?.nationality),
  // documentSubType is the combined DOT+DOS form field:
  // - National ID selected: value = '23' (DOT=23, DOS='' on submit)
  // - Passport subtype selected: value = DOS subtype (DOT=1, DOS=value on submit)
  [ApisFormFieldKey.DocumentSubType]:
    apisInfo?.documentType === DocumentType.NATIONAL_ID
      ? DocumentType.NATIONAL_ID
      : toStr(apisInfo?.documentSubType),

  // Passport/Document Details
  [ApisFormFieldKey.DocumentNumber]: toStr(apisInfo?.documentNumber),
  [ApisFormFieldKey.FirstName]: toStr(apisInfo?.firstName),
  [ApisFormFieldKey.MiddleName]: toStr(apisInfo?.middleName),
  [ApisFormFieldKey.LastName]: toStr(apisInfo?.lastName),
  [ApisFormFieldKey.Gender]: toStr(apisInfo?.gender),
  [ApisFormFieldKey.DateOfBirth]: toStr(apisInfo?.dateOfBirth),
  [ApisFormFieldKey.CountryOfIssue]: toStr(apisInfo?.issuingCountry),
  [ApisFormFieldKey.PassportExpiryDate]: toStr(apisInfo?.documentExpiry),

  // Visa Information
  [ApisFormFieldKey.VisaType]: toStr(apisInfo?.visaType),
  [ApisFormFieldKey.VisaNumber]: toStr(apisInfo?.visaNumber),
  [ApisFormFieldKey.VisaExpiryDate]: toStr(apisInfo?.visaExpiry),
  [ApisFormFieldKey.CountryOfVisa]: toStr(apisInfo?.visaCountryOfIssue),
});

const APIS_FIELD_ERROR_CONFIG: Partial<
  Record<
    ApisFieldCode,
    {
      formKey: ApisFormFieldKey;
      errorKey: string;
      messageKey: string;
      errorTranslationKey?: string;
    }
  >
> = {
  [ApisFieldCode.LNM]: {
    formKey: ApisFormFieldKey.LastName,
    errorKey: ApisFormFieldKey.LastName,
    messageKey: 'PassengerInfo.form.Lastname.Text',
  },
  [ApisFieldCode.FNM]: {
    formKey: ApisFormFieldKey.FirstName,
    errorKey: ApisFormFieldKey.FirstName,
    messageKey: 'PassengerInfo.form.Firstname.Text',
  },
  [ApisFieldCode.DON]: {
    formKey: ApisFormFieldKey.DocumentNumber,
    errorKey: ApisFormFieldKey.DocumentNumber,
    messageKey: 'PassengerInfo.form.PassportNumber.Text',
  },
  [ApisFieldCode.DOB]: {
    formKey: ApisFormFieldKey.DateOfBirth,
    errorKey: ApisFormFieldKey.DateOfBirth,
    messageKey: 'PassengerInfo.form.Birth_date.Text',
  },
  [ApisFieldCode.DOT]: {
    formKey: ApisFormFieldKey.DocumentSubType,
    errorKey: ApisFormFieldKey.DocumentSubType,
    messageKey: 'Validation.DocumentTypeRequired',
  },
  [ApisFieldCode.DOS]: {
    formKey: ApisFormFieldKey.DocumentSubType,
    errorKey: ApisFormFieldKey.DocumentSubType,
    messageKey: 'Please enter document',
  },
  [ApisFieldCode.NAT]: {
    formKey: ApisFormFieldKey.Nationality,
    errorKey: ApisFormFieldKey.Nationality,
    messageKey: 'PassengerInfo.form.Nationality.Text',
  },
  [ApisFieldCode.GEN]: {
    formKey: ApisFormFieldKey.Gender,
    errorKey: ApisFormFieldKey.Gender,
    messageKey: 'PassengerInfo.form.Gender.Text',
  },
  [ApisFieldCode.DOE]: {
    formKey: ApisFormFieldKey.PassportExpiryDate,
    errorKey: ApisFormFieldKey.PassportExpiryDate,
    messageKey: 'PassengerInfo.form.PassportExpiry.Text',
    errorTranslationKey: 'Form.Field-Required-v2',
  },
  [ApisFieldCode.COI]: {
    formKey: ApisFormFieldKey.CountryOfIssue,
    errorKey: ApisFormFieldKey.CountryOfIssue,
    messageKey: 'PassengerInfo.form.IssuingCountry.Text',
    errorTranslationKey: 'Form.Field-Required-v2',
  },
  [ApisFieldCode.VIT]: {
    formKey: ApisFormFieldKey.VisaType,
    errorKey: ApisFormFieldKey.VisaType,
    messageKey: 'PassengerInfo.form.VisaType.Text',
  },
  [ApisFieldCode.VIN]: {
    formKey: ApisFormFieldKey.VisaNumber,
    errorKey: ApisFormFieldKey.VisaNumber,
    messageKey: 'PassengerInfo.form.visa_num.Text',
  },
  [ApisFieldCode.VDE]: {
    formKey: ApisFormFieldKey.VisaExpiryDate,
    errorKey: ApisFormFieldKey.VisaExpiryDate,
    messageKey: 'PassengerInfo.form.visa_expiry.Text',
  },
  [ApisFieldCode.VCI]: {
    formKey: ApisFormFieldKey.CountryOfVisa,
    errorKey: ApisFormFieldKey.CountryOfVisa,
    messageKey: 'PassengerInfo.form.visa_country.Text',
  },
};

/** Skip mandatory VIT errors when nationality is in euList (field is hidden for EU). */
const shouldSkipRequiredVitError = (
  apisValues: Record<string, string>,
  euList: string[] | undefined,
): boolean => {
  if (!euList || euList.length === 0) {
    return false;
  }

  const nationality = apisValues[ApisFormFieldKey.Nationality]?.trim().toUpperCase() ?? '';
  if (!nationality) {
    return false;
  }

  return euList.some((countryCode) => countryCode.trim().toUpperCase() === nationality);
};

export const getCurrentPassengerApisErrors = (
  apisValues: Record<string, string>,
  requiredFieldCodes: string[] | undefined,
  euList?: string[],
): Record<string, string> => {
  const errors: Record<string, string> = {};

  (requiredFieldCodes ?? []).forEach((fieldCodeValue) => {
    const fieldCode = fieldCodeValue as ApisFieldCode;
    const config = APIS_FIELD_ERROR_CONFIG[fieldCode];

    if (!config) {
      return;
    }

    // EU nationality + VIT required: do not validate hidden Visa Type.
    if (fieldCode === ApisFieldCode.VIT && shouldSkipRequiredVitError(apisValues, euList)) {
      return;
    }

    const value = apisValues[config.formKey]?.trim();
    if (!value) {
      const translationKey = config.errorTranslationKey ?? 'Form.Field-Required';
      const rawFieldLabel = getResource(config.messageKey);
      const fieldLabel =
        fieldCode === ApisFieldCode.DOE ? rawFieldLabel.toLocaleLowerCase() : rawFieldLabel;

      errors[config.errorKey] = getResource(translationKey, {
        field: fieldLabel,
      });
    }
  });

  return errors;
};

export interface DangerousGoodsPopupContent {
  title: string;
  subtitle: string;
  imageUrl: string;
  imageAlt: string;
  subheader?: string;
  closeLabel: string;
}

export interface CheckInAgreementContent {
  title: string;
  sections: DynamicFormSection[];
  dangerousGoodsPopup?: DangerousGoodsPopupContent;
}

const TRAVEL_DOCUMENTS_QUESTION_KEY = 'Q_TRAVEL_DOC_REQUIRED';
const DANGEROUS_GOODS_QUESTION_KEY = 'Q_DANGEROUS_GOODS';

const normalizeTridionMediaUrl = (value: string | undefined): string => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedValue) || trimmedValue.startsWith('/tridionfeed/')) {
    return trimmedValue;
  }

  const normalizedPath = trimmedValue.startsWith('/') ? trimmedValue : `/${trimmedValue}`;
  return `/tridionfeed${normalizedPath}`;
};

const mapDangerousGoodsPopupContent = (
  entries: Question[],
): DangerousGoodsPopupContent | undefined => {
  const popupEntry = entries.find((entry) => entry.LinkText && entry.popover.IsTim === false);

  if (!popupEntry) {
    return undefined;
  }

  const title = popupEntry.popover.Key ?? ''; // append "tridionfeed"
  const imageUrl = normalizeTridionMediaUrl(
    popupEntry.popover.ImageUrl ?? popupEntry.popover.TBImageUrl ?? popupEntry.popover.MBImageUrl,
  );

  if (!title || !imageUrl) {
    return undefined;
  }

  return {
    title: getResource(title),
    subheader: getResource(popupEntry.popover.Value ?? ''),
    subtitle: '',
    imageUrl,
    imageAlt: getResource(title),
    closeLabel: getResource('Close.Text'),
  };
};

/**
 * Maps static check-in agreement content into DynamicFormFields sections.
 * This keeps agreement text/link composition in the shared passenger-details mapper.
 */
export const mapCheckInAgreementContent = (): CheckInAgreementContent => {
  const questionnaire: Questionnaire = selectQuestionnaire(useMasterDataStore.getState());

  const getItems = (key: string): Question[] =>
    Object.entries(questionnaire)
      .filter(([k]) => k === key)
      .flatMap(([, entry]) => Array.from(entry.items));

  const travelDocumentsQuestionEntries = getItems(TRAVEL_DOCUMENTS_QUESTION_KEY);
  const dangerousGoodsQuestionEntries = getItems(DANGEROUS_GOODS_QUESTION_KEY);

  const travelDocumentsFields = travelDocumentsQuestionEntries.map((entry, index) => ({
    key: `travel-documents-agreement-${index}`,
    col: 'col-12',
    label: '',
    type: 'checkInAgreement',
    agreementText: entry.Value ?? '',
    linkText: entry.LinkText ?? '',
    linkHref: entry.LinkText,
    showDividerAbove: index > 0,
  })) as unknown as DynamicFieldConfig[];
  const dangerousGoodsFields = dangerousGoodsQuestionEntries.map((entry, index) => ({
    key: `dangerous-goods-agreement-${index}`,
    col: 'col-12',
    label: '',
    type: 'checkInAgreement',
    agreementText: entry.Value ?? '',
    linkText: entry.LinkText ?? '',
    showDividerAbove: index > 0,
  })) as unknown as DynamicFieldConfig[];

  return {
    title: getResource('CheckIn.Agreements.Header.Text'),
    dangerousGoodsPopup: mapDangerousGoodsPopupContent(dangerousGoodsQuestionEntries),
    sections: [
      {
        id: 'travel-documents-agreement',
        title: getResource('CheckIn.Agreement.TravelDocuments.Header.Text'),
        fields: travelDocumentsFields,
      },
      {
        id: 'dangerous-goods-agreement',
        title: getResource('CheckIn.Agreement.DangerousGoods.Header.Text'),
        fields: dangerousGoodsFields,
      },
    ],
  };
};

// ============================================================================
// VISA REQUIREMENTS POPUP FORM
// ============================================================================

export interface VisaRequirementsDropdownOptions {
  citizenship: { label: string; value: string }[];
  residency: { label: string; value: string }[];
  destination: { label: string; value: string }[];
  documentType: { label: string; value: string }[];
  transitCountries: { label: string; value: string }[];
}

interface VisaDropdownOption {
  label: string;
  value: string;
}

export const VISA_REQUIREMENTS_FORM_KEYS = {
  citizenship: 'visa-citizenship',
  residency: 'visa-residency',
  destination: 'visa-destination',
  documentType: 'visa-document-type',
  transitCountries: 'visa-transit-countries',
} as const;

export const EMPTY_VISA_REQUIREMENTS_OPTIONS: VisaRequirementsDropdownOptions = {
  citizenship: [],
  residency: [],
  destination: [],
  documentType: [],
  transitCountries: [],
};

export const mapCountriesToVisaDropdownOptions = (countries: Country[]): VisaDropdownOption[] =>
  countries
    .filter((country) => !country.isDisabled)
    .map((country) => ({
      label: getResource(country.name),
      value: country.code,
    }));

export const mapVisaDocumentTypesToDropdownOptions = (
  passportSubTypes: { key: string; value: string }[],
): VisaDropdownOption[] =>
  passportSubTypes.map((item) => ({
    label: getResource(item.value),
    value: item.key,
  }));

export const mapVisaRequirementsValuesToTimPayload = (
  values: Record<string, string>,
): TimRequest => {
  const transitValue = values[VISA_REQUIREMENTS_FORM_KEYS.transitCountries] ?? '';
  const transitPoints = transitValue
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return {
    nationality: values[VISA_REQUIREMENTS_FORM_KEYS.citizenship] ?? '',
    residenceCtry: values[VISA_REQUIREMENTS_FORM_KEYS.residency] ?? '',
    destCtry: values[VISA_REQUIREMENTS_FORM_KEYS.destination] ?? '',
    documentType: values[VISA_REQUIREMENTS_FORM_KEYS.documentType] ?? '',
    transitPoints,
  };
};

const buildVisaSelectField = (
  key: string,
  col: string,
  labelKey: string,
  fieldOptions: VisaDropdownOption[],
  extras: { required?: boolean; multiSelect?: boolean } = {},
): Record<string, unknown> => ({
  type: 'select',
  key,
  col,
  label: getResource(labelKey),
  placeholder: getResource('Select.Text'),
  options: fieldOptions,
  searchable: true,
  searchMatchMode: 'startsWith',
  ...extras,
});

export const buildVisaRequirementsPopupSections = (
  options: VisaRequirementsDropdownOptions,
): DynamicFormSection[] => [
  {
    id: 'visa-requirements-form',
    // DS styles target `.visa-input-section` for responsive col-4/col-8 layout.
    // `plain` avoids nested white cards inside ActionPopup (DS visa modal pattern).
    className: 'visa-input-section',
    sectionVariant: 'plain',
    fields: [
      buildVisaSelectField(
        VISA_REQUIREMENTS_FORM_KEYS.citizenship,
        'col-4',
        'VisaRequirements.PassportHolderOf.Text',
        options.citizenship,
        { required: true },
      ),
      buildVisaSelectField(
        VISA_REQUIREMENTS_FORM_KEYS.residency,
        'col-4',
        'VisaRequirements.Residency.Text',
        options.residency,
        { required: true },
      ),
      buildVisaSelectField(
        VISA_REQUIREMENTS_FORM_KEYS.destination,
        'col-4',
        'VisaRequirements.TravellingTo.Text',
        options.destination,
        { required: true },
      ),
      buildVisaSelectField(
        VISA_REQUIREMENTS_FORM_KEYS.documentType,
        'col-4',
        'PassengerInfo.form.DocumentType.Text',
        options.documentType,
        { required: true },
      ),
      buildVisaSelectField(
        VISA_REQUIREMENTS_FORM_KEYS.transitCountries,
        'col-8',
        'VisaRequirements.AddTransitCountries.Text',
        options.transitCountries,
        { multiSelect: true },
      ),
    ] as unknown as DynamicFormSection['fields'],
  },
];

export interface VisaRequirementSectionView {
  title: string;
  paragraphs: string[];
}

export interface VisaRequirementCountryView {
  countryName: string;
  sections: VisaRequirementSectionView[];
}

export interface VisaRequirementResultTabView {
  id: string;
  label: string;
  countries: VisaRequirementCountryView[];
}

const TIMATIC_NON_SECTION_KEYS = new Set(['countryName', 'additionalInfo']);

const toTimaticTabLabel = (categoryId: string): string =>
  categoryId.length === 0
    ? categoryId
    : `${categoryId.charAt(0).toUpperCase()}${categoryId.slice(1)}`;

/** Tab titles re-translate on next search; TIM body stays provider/English. */
const toTimaticTabTitle = (categoryId: string): string => {
  const fallback = toTimaticTabLabel(categoryId);
  return getResource(`VisaRequirements.${fallback}.Text`, undefined, fallback);
};

const isNonEmptySectionArray = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0;

/** Timatic /en returns English names — map to localized master-data label when available. */
const resolveTimaticCountryDisplayName = (timCountryName: string, countries: Country[]): string => {
  const normalized = timCountryName.trim().toLowerCase();
  if (!normalized) {
    return timCountryName;
  }

  const match = countries.find((country) => {
    const englishName = String((country as Country & { englishName?: string }).englishName ?? '')
      .trim()
      .toLowerCase();
    const localizedName = country.name.trim().toLowerCase();
    return englishName === normalized || localizedName === normalized;
  });

  return match?.name ?? timCountryName;
};

/** Discover tab ids from response section arrays that have content in any country. */
const discoverTimaticCategoryIds = (countries: Record<string, unknown>[]): string[] => {
  const seen = new Set<string>();

  for (const country of countries) {
    for (const [key, value] of Object.entries(country)) {
      if (TIMATIC_NON_SECTION_KEYS.has(key) || !isNonEmptySectionArray(value)) {
        continue;
      }
      seen.add(key);
    }
  }

  return Array.from(seen);
};

const mapTimaticCategoryCountries = (
  countries: Record<string, unknown>[],
  categoryId: string,
  masterCountries: Country[],
): VisaRequirementCountryView[] =>
  countries
    .filter((country) => isNonEmptySectionArray(country[categoryId]))
    .map((country) => {
      const sections = country[categoryId] as {
        sectionTitle?: string;
        sectionParagraph?: { paragraph?: string }[];
      }[];
      const rawCountryName = String(country.countryName ?? '');

      return {
        countryName: resolveTimaticCountryDisplayName(rawCountryName, masterCountries),
        sections: sections.map((section) => ({
          title: section.sectionTitle ?? '',
          paragraphs: (section.sectionParagraph ?? []).map(
            (paragraph) => paragraph.paragraph ?? '',
          ),
        })),
      };
    });

/**
 * Maps TimaticQuery → ActionPopup `visaRequirementResults` props.
 * Tabs come from data (any non-empty category); HTML stays in the design system.
 * Country headings are localized via master-data countrylist (TIM body stays English).
 */
export const mapTimaticToVisaRequirementResults = (
  response: unknown,
  masterCountries: Country[] = [],
): VisaRequirementResultTabView[] => {
  const countries =
    (
      response as {
        timaticQuery?: { timatic?: Record<string, unknown>[] };
      }
    )?.timaticQuery?.timatic ?? [];

  return discoverTimaticCategoryIds(countries).map((categoryId) => ({
    id: categoryId,
    label: toTimaticTabTitle(categoryId),
    countries: mapTimaticCategoryCountries(countries, categoryId, masterCountries),
  }));
};

/**
 * Maps EmergencyContactDetails to ContactDetailsSection emergency mode props
 */
export const mapEmergencyContactToProps = (
  emergencyContact: EmergencyContactDetails | undefined,
): {
  fullName: string;
  relationship: string;
  formState: {
    email: string;
    phone: string;
    countryCode: string;
  };
} => {
  return {
    fullName: emergencyContact?.contactName ?? '',
    relationship: emergencyContact?.contactRelation ?? '',
    formState: {
      email: '',
      phone: emergencyContact?.contactNumber ?? '',
      countryCode: emergencyContact?.countryCode ?? '',
    },
  };
};

// ============================================================================
// DYNAMIC FORM FIELDS MAPPER
// ============================================================================

/**
 * Gets gender options with i18n labels
 */
const getGenderOptions = (): { label: string; value: string }[] => [
  { label: getResource('PassengerInfo.form.Male.Text'), value: 'M' },
  { label: getResource('PassengerInfo.form.Female.Text'), value: 'F' },
];

/**
 * Extracts visa type options from master data resourceLists
 * Falls back to default options if not available
 */
const getVisaTypeOptions = (): { label: string; value: string }[] => {
  const resourceLists = selectResourceLists(useMasterDataStore.getState());
  const visaTypes = resourceLists?.VisaType;
  if (visaTypes && visaTypes.length > 0) {
    return visaTypes.map((item) => ({ label: item.value, value: item.key }));
  }
  return [];
};

/**
 * Extracts country options from master data countries list
 * Filters out disabled countries and maps to dropdown format
 */
const getCountryOptions = (): { label: string; value: string }[] => {
  const countries = selectCountries(useMasterDataStore.getState());
  return countries
    .filter((country) => !country.isDisabled)
    .map((country) => ({
      label: country.name,
      value: country.alphaNumeric3 ?? country.code, // Fallback to code if alphaNumeric3 is not available
    }));
};

// Document type options are generated dynamically based on nationality
// See getDocumentTypeOptions() helper function

/**
 * Helper to check if a field should be displayed based on apisFields
 */
const isFieldVisible = (fieldCode: string, apisFields?: ApisFields): boolean => {
  if (!apisFields) return false;
  return apisFields.required.includes(fieldCode) || apisFields.optional.includes(fieldCode);
};

/**
 * Helper to check if a field is required
 */
const isFieldRequired = (fieldCode: string, apisFields?: ApisFields): boolean => {
  if (!apisFields) return false;
  return apisFields.required.includes(fieldCode);
};

/**
 * Helper to build validation rules for a field
 * @param fieldLabelKey - i18n key for the field label, used to produce
 *   field-specific required messages via "Form.Field-Required" → "{{field}} is required."
 */
const buildValidationRules = (
  fieldCode: string,
  apisFields?: ApisFields,
  additionalRules: ValidationRule[] = [],
  fieldLabelKey?: string,
): ValidationRule[] => {
  const rules: ValidationRule[] = [];

  if (isFieldRequired(fieldCode, apisFields)) {
    const fieldName = fieldLabelKey ? getResource(fieldLabelKey) : undefined;
    rules.push({
      type: 'required',
      message: getResource('Form.Field-Required', fieldName ? { field: fieldName } : undefined),
    });
  }

  // Defensive: only add objects with a 'type' property
  const result: ValidationRule[] = [];
  for (const rule of rules) {
    result.push(rule);
  }
  if (Array.isArray(additionalRules)) {
    for (const r of additionalRules) {
      if (typeof r === 'object' && r !== null && 'type' in r) {
        result.push(r);
      }
    }
  }
  return result;
};

/**
 * Maps NotCheckedInPax and ValidationRules to DynamicFormSection[] config
 * for use with DynamicFormFields component from @fz/ui-components-react
 *
 * @param passenger - Passenger data with apisInfo
 * @param validationRules - Validation rules containing apisFields configuration
 * @returns Array of sections matching DynamicFormFields schema
 */
// ============================================================================
// DYNAMIC FORM FIELDS MAPPER - Helper Functions
// ============================================================================

/**
 * Builds grouped document type options (Passport subtypes + optional National ID).
 * Uses `groups` to render a categorised Dropdown (no search bar).
 * - Passport group: passport subtypes from resourceLists.PassportSubType
 * - National ID group: single option, shown only for GCC nationals or masked GCC
 */
const shouldShowNationalId = (
  nationality?: string,
  isGCCNational?: boolean | null,
  gccList?: string[],
): boolean => {
  const normalizedNationality = nationality?.trim().toUpperCase() ?? '';
  const normalizedGccList = (gccList ?? []).map((countryCode) => countryCode.trim().toUpperCase());

  if (normalizedNationality.length > 0) {
    return normalizedGccList.includes(normalizedNationality);
  }

  return isGCCNational === true;
};

const GCC_NATIONALITY_NOTE_KEY = 'PassengerInfo.form.GCCNationalityNote.Text';
const VISA_TYPE_DISCLAIMER_KEY = 'SelectPassenger.Disclaimer.Text';
const VISA_TYPE_DISCLAIMER_CONTENT_KEY = 'PassengerInfo.Disclaimer.content.Text';

const createGccNationalityNote = (): ReactElement =>
  createElement(
    Label,
    {
      className: 'body5',
      variant: 'tertiary',
      fontWeightStyle: 'regular',
    },
    getResource(GCC_NATIONALITY_NOTE_KEY),
  );

/** Required VIT: show only for a selected non-EU nationality. */
const isRequiredVitVisible = (
  isMaskedNationality: boolean,
  isEuNationalitySelected: boolean,
): boolean => !isMaskedNationality && !isEuNationalitySelected;

/**
 * Angular showVisaType: VIT visible and (non-EU nationality, or masked + not EU).
 * When VIT is required: show only after nationality is selected, and never for EU nationality.
 */
export const showVisaType = (
  apisFields: ApisFields | undefined,
  nationality: string | undefined,
  isEUNational: boolean | undefined,
  euList: string[] | undefined,
): boolean => {
  if (!isFieldVisible(ApisFieldCode.VIT, apisFields)) {
    return false;
  }

  const normalizedNationality = nationality?.trim().toUpperCase() ?? '';
  const isMaskedNationality = normalizedNationality.length === 0;
  const normalizedEuList = (euList ?? []).map((countryCode) => countryCode.trim().toUpperCase());
  const isEuNationalitySelected =
    !isMaskedNationality && normalizedEuList.includes(normalizedNationality);

  if (isFieldRequired(ApisFieldCode.VIT, apisFields)) {
    return isRequiredVitVisible(isMaskedNationality, isEuNationalitySelected);
  }

  if (!isMaskedNationality && !isEuNationalitySelected) {
    return true;
  }

  return isEUNational === false && isMaskedNationality;
};

const getDocumentTypeGroups = (
  nationality?: string,
  isGCCNational?: boolean | null,
  gccList?: string[],
): SelectOptionGroup[] => {
  const resourceLists = selectResourceLists(useMasterDataStore.getState());
  const passportSubTypes = resourceLists?.PassportSubType ?? [];

  const passportOptions =
    passportSubTypes.length > 0
      ? passportSubTypes.map((item) => ({ label: item.value, value: item.key }))
      : [{ label: getResource('PassengerInfo.form.PassportSubType.Text'), value: '1' }];

  const groups: SelectOptionGroup[] = [
    {
      groupLabel: getResource('PassengerInfo.form.PassportSubType.Text'),
      options: passportOptions,
    },
  ];
  const showNationalId = shouldShowNationalId(nationality, isGCCNational, gccList);
  if (showNationalId) {
    groups.push({
      groupLabel: getResource('PassengerInfo.form.NationalID.Text'),
      options: [
        {
          label: getResource('PassengerInfo.form.NationalID.Text'),
          value: DocumentType.NATIONAL_ID,
        },
      ],
    });
  }

  return groups;
};

/**
 * Builds Section 1: Basic Information (nationality, document type+subtype)
 */
const buildBasicInfoSection = (
  apisFields?: ApisFields,
  nationality?: string,
  isGCCNational?: boolean | null,
  gccList?: string[],
): DynamicFormSection => {
  const fields: DynamicFieldConfig[] = [];
  const showGccNationalityNote =
    isFieldVisible(ApisFieldCode.NAT, apisFields) &&
    shouldShowNationalId(nationality, isGCCNational, gccList);

  if (isFieldVisible(ApisFieldCode.NAT, apisFields)) {
    fields.push({
      type: FieldType.SELECT,
      key: ApisFormFieldKey.Nationality,
      col: 'col-6',
      label: getResource('PassengerInfo.form.Nationality.Text'),
      placeholder: getResource('Select.Text'),
      required: isFieldRequired(ApisFieldCode.NAT, apisFields),
      searchable: true,
      searchMatchMode: 'startsWith',
      noOptionsLabel: {
        children: getResource('NoResult.Text'),
      },
      options: getCountryOptions(),
      validation: buildValidationRules(
        ApisFieldCode.NAT,
        apisFields,
        [],
        'PassengerInfo.form.Nationality.Text',
      ),
    });
  }

  // DOT + DOS combined into one grouped dropdown (no search bar)
  // Passport group: passport subtypes from resourceLists.PassportSubType
  // National ID group: single option, shown only for GCC nationals
  if (isFieldVisible(ApisFieldCode.DOT, apisFields)) {
    fields.push({
      type: FieldType.SELECT,
      key: ApisFormFieldKey.DocumentSubType,
      col: 'col-6',
      label: getResource('PassengerInfo.form.DocumentType.Text'),
      placeholder: getResource('Select.Text'),
      required: isFieldRequired(ApisFieldCode.DOT, apisFields),
      groups: getDocumentTypeGroups(nationality, isGCCNational, gccList),
      validation: buildValidationRules(
        ApisFieldCode.DOT,
        apisFields,
        [],
        'PassengerInfo.form.DocumentType.Text',
      ),
    });
  }
  return fields.length > 0
    ? {
        id: ApisFormSectionId.BasicInfo,
        fields,
        sectionNote: showGccNationalityNote ? createGccNationalityNote() : undefined,
      }
    : { id: ApisFormSectionId.BasicInfo, fields: [] };
};

/**
 * Builds Section 2: Passport Details
 * Complex due to multiple field configurations - refactoring would reduce readability
 */

// --- Passport field creators (modular, like visa section) ---
function createDocumentNumberField(
  apisFields?: ApisFields,
  documentType?: string,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.DON, apisFields)) return null;
  if (documentType !== DocumentType.PASSPORT && documentType !== DocumentType.NATIONAL_ID)
    return null;
  const isNationalId = documentType === DocumentType.NATIONAL_ID;
  const labelKey = isNationalId
    ? 'PassengerInfo.form.DocumentNumber.Text'
    : 'PassengerInfo.form.PassportNumber.Text';
  const placeholderKey = isNationalId
    ? 'PassengerInfo.form.EnterDocumentNumber.Text'
    : 'PassengerInfo.form.Enter.Text';

  const validationError = isNationalId
    ? getResource('Form.Field-Required', { field: 'document number' })
    : getResource('Form.Field-Required', { field: 'passport number' });
  return {
    type: FieldType.TEXT,
    key: ApisFormFieldKey.DocumentNumber,
    col: 'col-6',
    label: getResource(labelKey),
    inputRestriction: 'alphanumeric',
    placeholder: getResource(placeholderKey),
    inputType: 'text',
    autoCapitalize: true,
    required: isFieldRequired(ApisFieldCode.DON, apisFields),
    maxLength: 20,
    onSetError: (_key, _error): void => {
      const fieldKey =
        typeof _key === 'string' && _key.trim().length > 0 ? _key : ApisFormFieldKey.DocumentNumber;
      onFieldError?.({
        passengerIndex,
        fieldKey,
        documentType,
        fieldError: _error,
      });
    },
    validation: buildValidationRules(
      ApisFieldCode.DON,
      apisFields,
      [
        {
          type: 'pattern',
          value: /^[A-Za-z0-9]+$/,
          message: validationError,
        },
        {
          type: 'maxLength',
          value: 20,
          message: validationError,
        },
      ],
      labelKey,
    ),
  };
}

function createFirstNameField(
  apisFields?: ApisFields,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.FNM, apisFields)) return null;
  return {
    type: FieldType.TEXT,
    key: ApisFormFieldKey.FirstName,
    col: 'col-6',
    label: getResource('PassengerInfo.form.Firstname.Text'),
    placeholder: getResource('PassengerInfo.form.Enter.Text'),
    inputType: 'text',
    required: isFieldRequired(ApisFieldCode.FNM, apisFields),
    maxLength: 64,
    inputRestriction: 'alphabets',
    onSetError: (_key, _error): void => {
      const fieldKey =
        typeof _key === 'string' && _key.trim().length > 0 ? _key : ApisFormFieldKey.FirstName;
      onFieldError?.({
        passengerIndex,
        fieldKey,
        fieldError: _error,
      });
    },
    validation: buildValidationRules(
      ApisFieldCode.FNM,
      apisFields,
      [
        {
          type: 'pattern',
          value: /^[A-Za-z\s]+$/,
          message: getResource('Form.Field-Required', { field: 'first name' }),
        },
        {
          type: 'maxLength',
          value: 64,
          message: getResource('Form.Field-Required', { field: 'first name' }),
        },
      ],
      'PassengerInfo.form.Firstname.Text',
    ),
  };
}

function createMiddleNameField(apisFields?: ApisFields): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.MNM, apisFields)) return null;
  return {
    type: FieldType.TEXT,
    key: ApisFormFieldKey.MiddleName,
    col: 'col-6',
    label: `${getResource('PassengerInfo.form.MiddleName.Text')} (${getResource('PassengerInfo.form.optional.Text')})`,
    placeholder: getResource('PassengerInfo.form.MiddleName.Text'),
    inputType: 'text',
    inputRestriction: 'alphabets',
    required: false,
    maxLength: 64,
    validation: [
      {
        type: 'pattern',
        value: /^[A-Za-z\s]+$/,
        message: getResource('Form.Field-Required', { field: 'middle name' }),
      },
      {
        type: 'maxLength',
        value: 64,
        message: getResource('Form.Field-Required', { field: 'middle name' }),
      },
    ],
  };
}

function createLastNameField(
  apisFields?: ApisFields,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.LNM, apisFields)) return null;
  return {
    type: FieldType.TEXT,
    key: ApisFormFieldKey.LastName,
    col: 'col-6',
    label: getResource('PassengerInfo.form.Lastname.Text'),
    placeholder: getResource('PassengerInfo.form.Enter.Text'),
    inputType: 'text',
    required: isFieldRequired(ApisFieldCode.LNM, apisFields),
    maxLength: 64,
    inputRestriction: 'alphabets',
    onSetError: (_key, _error): void => {
      const fieldKey =
        typeof _key === 'string' && _key.trim().length > 0 ? _key : ApisFormFieldKey.LastName;
      onFieldError?.({
        passengerIndex,
        fieldKey,
        fieldError: _error,
      });
    },
    validation: buildValidationRules(
      ApisFieldCode.LNM,
      apisFields,
      [
        {
          type: 'pattern',
          value: /^[A-Za-z\s]+$/,
          message: getResource('Form.Field-Required', { field: 'last name' }),
        },
        {
          type: 'maxLength',
          value: 64,
          message: getResource('ErrorMessage.ContactName.Text'),
        },
      ],
      'PassengerInfo.form.Lastname.Text',
    ),
  };
}

function createGenderField(apisFields?: ApisFields): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.GEN, apisFields)) return null;
  return {
    type: FieldType.SELECT,
    key: ApisFormFieldKey.Gender,
    col: 'col-6',
    label: getResource('PassengerInfo.form.Gender.Text'),
    placeholder: getResource('Select.Text'),
    required: isFieldRequired(ApisFieldCode.GEN, apisFields),
    options: getGenderOptions(),
    validation: buildValidationRules(
      ApisFieldCode.GEN,
      apisFields,
      [],
      'PassengerInfo.form.Gender.Text',
    ),
  };
}

const DOB_ADULT_MIN_YEARS = -120;
const DOB_ADULT_MAX_YEARS = -12;
const DOB_CHILD_MIN_YEARS = -12;
const DOB_CHILD_MAX_YEARS = -2;
const DOB_INFANT_MIN_YEARS = -2;
const DOB_INFANT_MAX_DAYS = -7;
const DOE_MAX_YEARS = 50;

/**
 * Builds min/max Date bounds for DOB based on passenger type and flight date.
 * All ranges are relative to the flight date (fallback: today).
 *   Adult:  min = flightDate − 100yr,  max = flightDate − 12yr
 *   Child:  min = flightDate − 12yr,   max = flightDate − 2yr
 *   Infant: min = flightDate − 2yr,    max = flightDate − 7 days
 */
const getDobBounds = (
  passengerType: PassengerTypeEnum,
  flightDate?: string,
): { minDate: Date; maxDate: Date } => {
  const ref = flightDate ? new Date(flightDate) : new Date();
  const d = (base: Date, years: number, days = 0): Date => {
    const result = new Date(base);
    result.setFullYear(result.getFullYear() + years);
    result.setDate(result.getDate() + days);
    return result;
  };
  if (passengerType === PassengerTypeEnum.Child) {
    return { minDate: d(ref, DOB_CHILD_MIN_YEARS), maxDate: d(ref, DOB_CHILD_MAX_YEARS) };
  }
  if (passengerType === PassengerTypeEnum.Infant) {
    return { minDate: d(ref, DOB_INFANT_MIN_YEARS), maxDate: d(ref, 0, DOB_INFANT_MAX_DAYS) };
  }
  // Adult (default)
  return { minDate: d(ref, DOB_ADULT_MIN_YEARS), maxDate: d(ref, DOB_ADULT_MAX_YEARS) };
};

/**
 * Builds min/max Date bounds for DOE (document expiry).
 *   min = today
 *   max = today + 20 years
 */
const getToday = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getDoeBounds = (): { minDate: Date; maxDate: Date } => {
  const today = getToday();
  const maxDate = new Date(today);
  maxDate.setFullYear(maxDate.getFullYear() + DOE_MAX_YEARS);
  return { minDate: today, maxDate };
};

function createDateOfBirthField(
  apisFields?: ApisFields,
  passengerType?: PassengerTypeEnum,
  flightDate?: string,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.DOB, apisFields)) return null;
  const bounds =
    passengerType !== null && passengerType !== undefined
      ? getDobBounds(passengerType, flightDate)
      : undefined;
  const fieldLabel = getResource('PassengerInfo.form.Birth_date.Text');
  const invalidDateError = getResource('Form.Field-Invalid').replace('{{field}}', fieldLabel);
  return {
    type: FieldType.DATE,
    key: ApisFormFieldKey.DateOfBirth,
    col: 'col-6',
    label: fieldLabel,
    calendarTitle: getResource('PassengerInfo.form.SelectBirth_date.Text'),
    confirmLabel: getResource('Confirm.Text'),
    placeholder: getResource('Select.Text'),
    required: isFieldRequired(ApisFieldCode.DOB, apisFields),
    ...(bounds ?? {}),
    onSetError: createDateFieldErrorHandler(
      passengerIndex,
      ApisFormFieldKey.DateOfBirth,
      onFieldError,
    ),
    onInvalidDate: (): void => {
      onFieldError?.({
        passengerIndex,
        fieldKey: ApisFormFieldKey.DateOfBirth,
        fieldError: invalidDateError,
      });
    },
    invalidDateError,
    validation: buildValidationRules(
      ApisFieldCode.DOB,
      apisFields,
      [],
      'PassengerInfo.form.Birth_date.Text',
    ),
  };
}

function createCountryOfIssueField(apisFields?: ApisFields): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.COI, apisFields)) return null;
  return {
    type: FieldType.SELECT,
    key: ApisFormFieldKey.CountryOfIssue,
    col: 'col-6',
    label: getResource('PassengerInfo.form.IssuingCountry.Text'),
    placeholder: getResource('Select.Text'),
    required: isFieldRequired(ApisFieldCode.COI, apisFields),
    searchable: true,
    searchMatchMode: 'startsWith',
    options: getCountryOptions(),
    validation: buildValidationRules(
      ApisFieldCode.COI,
      apisFields,
      [],
      'PassengerInfo.form.IssuingCountry.Text',
    ),
  };
}

const createDateFieldErrorHandler = (
  passengerIndex: number | undefined,
  fieldKey: string,
  onFieldError: FieldErrorHandler | undefined,
) => {
  return (_key: string, _error: string | undefined): void => {
    const resolvedKey = typeof _key === 'string' && _key.trim().length > 0 ? _key : fieldKey;
    onFieldError?.({
      passengerIndex,
      fieldKey: resolvedKey,
      fieldError: _error,
    });
    // Clear invalid date error when valid date is selected
    if (!_error) {
      onFieldError?.({
        passengerIndex,
        fieldKey: resolvedKey,
        fieldError: undefined,
      });
    }
  };
};

function createPassportExpiryField(
  apisFields?: ApisFields,
  documentType?: string,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (
    !isFieldVisible(ApisFieldCode.DOE, apisFields) ||
    !(documentType === DocumentType.PASSPORT || documentType === DocumentType.NATIONAL_ID)
  )
    return null;
  const labelKey =
    documentType === DocumentType.NATIONAL_ID
      ? 'PassengerInfo.form.DocumentExpiry.Text'
      : 'PassengerInfo.form.PassportExpiry.Text';
  const fieldLabel = getResource(labelKey);
  const invalidDateError = getResource('Form.Field-Invalid').replace('{{field}}', fieldLabel);
  const { minDate, maxDate } = getDoeBounds();
  return {
    type: FieldType.DATE,
    key: ApisFormFieldKey.PassportExpiryDate,
    col: 'col-6',
    label: fieldLabel,
    confirmLabel: getResource('Confirm.Text'),
    placeholder: getResource('Select.Text'),
    required: isFieldRequired(ApisFieldCode.DOE, apisFields),
    minDate,
    maxDate,
    onSetError: createDateFieldErrorHandler(
      passengerIndex,
      ApisFormFieldKey.PassportExpiryDate,
      onFieldError,
    ),
    onInvalidDate: (): void => {
      onFieldError?.({
        passengerIndex,
        fieldKey: ApisFormFieldKey.PassportExpiryDate,
        fieldError: invalidDateError,
      });
    },
    invalidDateError,
    validation: buildValidationRules(ApisFieldCode.DOE, apisFields, [], labelKey),
    tooltip:
      documentType === DocumentType.NATIONAL_ID
        ? getResource('PassengerInfo.form.ExpiryDateToolTipDescription.Text')
        : undefined,
  };
}

const pushIfFieldExists = (
  fields: DynamicFieldConfig[],
  field: DynamicFieldConfig | null,
): void => {
  if (field) {
    fields.push(field);
  }
};

const getPassportDetailsSectionTitle = (documentType?: string): string =>
  documentType === DocumentType.NATIONAL_ID
    ? 'PassengerInfo.form.NationalDetails.Text'
    : 'PassengerInfo.form.PassportDetails.Text';

const buildPassportSectionResult = (
  fields: DynamicFieldConfig[],
  sectionTitleKey: string,
): DynamicFormSection => {
  if (fields.length === 0) {
    return { id: ApisFormSectionId.PassportDetails, fields: [] };
  }

  return {
    id: ApisFormSectionId.PassportDetails,
    title: getResource(sectionTitleKey),
    subtitle: getResource('PassengerInfo.Mandatory.Text'),
    showDivider: true,
    fields,
  };
};

const buildPassportDetailsSection = (
  apisFields?: ApisFields,
  documentType?: string,
  passengerType?: PassengerTypeEnum,
  flightDate?: string,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFormSection => {
  const fields: DynamicFieldConfig[] = [];
  const passportFields: (DynamicFieldConfig | null)[] = [
    createDocumentNumberField(apisFields, documentType, passengerIndex, onFieldError),
    createFirstNameField(apisFields, passengerIndex, onFieldError),
    createMiddleNameField(apisFields),
    createLastNameField(apisFields, passengerIndex, onFieldError),
    createGenderField(apisFields),
    createDateOfBirthField(apisFields, passengerType, flightDate, passengerIndex, onFieldError),
    createCountryOfIssueField(apisFields),
    createPassportExpiryField(apisFields, documentType, passengerIndex, onFieldError),
  ];

  passportFields.forEach((field) => {
    pushIfFieldExists(fields, field);
  });

  return buildPassportSectionResult(fields, getPassportDetailsSectionTitle(documentType));
};

/**
 * Builds Section 3: Visa Information
 * Slightly over line limit due to multiple field configurations
 */

// --- Visa field creators (outside main function for complexity and type safety) ---
const createVisaFieldErrorHandler =
  (
    defaultFieldKey: ApisFormFieldKey,
    passengerIndex?: number,
    onFieldError?: FieldErrorHandler,
  ): ((_key: string, _error: unknown) => void) =>
  (_key, _error): void => {
    const fieldKey = typeof _key === 'string' && _key.trim().length > 0 ? _key : defaultFieldKey;
    onFieldError?.({
      passengerIndex,
      fieldKey,
      fieldError: _error,
    });
  };

function createVisaTypeField(
  apisFields: ApisFields | undefined,
  nationality: string | undefined,
  isEUNational: boolean | undefined,
  euList: string[] | undefined,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!showVisaType(apisFields, nationality, isEUNational, euList)) return null;
  return {
    type: FieldType.SELECT,
    key: ApisFormFieldKey.VisaType,
    col: 'col-6',
    label: getResource('PassengerInfo.form.VisaType.Text'),
    placeholder: getResource('Select.Text'),
    required: isFieldRequired(ApisFieldCode.VIT, apisFields),
    options: getVisaTypeOptions(),
    onSetError: createVisaFieldErrorHandler(
      ApisFormFieldKey.VisaType,
      passengerIndex,
      onFieldError,
    ),
    validation: buildValidationRules(
      ApisFieldCode.VIT,
      apisFields,
      [],
      'PassengerInfo.form.VisaType.Text',
    ),
  };
}

const createVisaTypeDisclaimerNote = (): ReactElement =>
  createElement(AlertBanner, {
    variant: 'custom-info',
    className: 'alert-banner--note',
    isExpandable: false,
    isExpanded: false,
    title: {
      children: `${getResource(VISA_TYPE_DISCLAIMER_KEY)}: ${getResource(VISA_TYPE_DISCLAIMER_CONTENT_KEY)}`,
      className: 'body4',
      fontWeightStyle: 'regular',
      variant: 'primary',
    },
  });

function createVisaNumberField(
  apisFields?: ApisFields,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.VIN, apisFields)) return null;
  const isRequired = isFieldRequired(ApisFieldCode.VIN, apisFields);
  const labels = isRequired
    ? getResource('PassengerInfo.form.visa_num.Text')
    : `${getResource('PassengerInfo.form.visa_num.Text')} (${getResource('PassengerInfo.form.optional.Text')})`;

  return {
    type: FieldType.TEXT,
    key: ApisFormFieldKey.VisaNumber,
    col: 'col-6',
    label: labels,
    inputRestriction: 'alphanumeric',
    autoCapitalize: true,
    placeholder: getResource('PassengerInfo.form.Enter.Text'),
    inputType: 'text',
    required: isRequired,
    maxLength: 20,
    onSetError: createVisaFieldErrorHandler(
      ApisFormFieldKey.VisaNumber,
      passengerIndex,
      onFieldError,
    ),
    validation: buildValidationRules(
      ApisFieldCode.VIN,
      apisFields,
      [
        {
          type: 'pattern',
          value: /^[A-Za-z0-9]+$/,
          message: getResource('ErrorMessage.MobileNumber.Text'),
        },
        {
          type: 'maxLength',
          value: 20,
          message: getResource('ErrorMessage.MobileNumber.Text'),
        },
      ],
      'PassengerInfo.form.visa_num.Text',
    ),
  };
}

function createVisaExpiryField(
  apisFields?: ApisFields,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.VDE, apisFields)) return null;

  const isRequired = isFieldRequired(ApisFieldCode.VDE, apisFields);
  const baseLabel = getResource('PassengerInfo.form.visa_expiry.Text');
  const labels = isRequired
    ? baseLabel
    : `${baseLabel} (${getResource('PassengerInfo.form.optional.Text')})`;
  const invalidDateError = getResource('Form.Field-Invalid').replace('{{field}}', baseLabel);

  return {
    type: FieldType.DATE,
    key: ApisFormFieldKey.VisaExpiryDate,
    col: 'col-6',
    label: labels,
    confirmLabel: getResource('Confirm.Text'),
    placeholder: getResource('Select.Text'),
    required: isRequired,
    minDate: getToday(),
    onSetError: createDateFieldErrorHandler(
      passengerIndex,
      ApisFormFieldKey.VisaExpiryDate,
      onFieldError,
    ),
    onInvalidDate: (): void => {
      onFieldError?.({
        passengerIndex,
        fieldKey: ApisFormFieldKey.VisaExpiryDate,
        fieldError: invalidDateError,
      });
    },
    invalidDateError,
    validation: buildValidationRules(
      ApisFieldCode.VDE,
      apisFields,
      [],
      'PassengerInfo.form.visa_expiry.Text',
    ),
  };
}

function createCountryOfVisaField(
  apisFields?: ApisFields,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFieldConfig | null {
  if (!isFieldVisible(ApisFieldCode.VCI, apisFields)) return null;
  const isRequired = isFieldRequired(ApisFieldCode.VCI, apisFields);
  const labels = isRequired
    ? getResource('PassengerInfo.form.visa_country.Text')
    : `${getResource('PassengerInfo.form.visa_country.Text')} (${getResource('PassengerInfo.form.optional.Text')})`;

  return {
    type: FieldType.SELECT,
    key: ApisFormFieldKey.CountryOfVisa,
    col: 'col-6',
    label: labels,
    placeholder: getResource('Select.Text'),
    required: isRequired,
    searchable: true,
    searchMatchMode: 'startsWith',
    options: getCountryOptions(),
    onSetError: createVisaFieldErrorHandler(
      ApisFormFieldKey.CountryOfVisa,
      passengerIndex,
      onFieldError,
    ),
    validation: buildValidationRules(
      ApisFieldCode.VCI,
      apisFields,
      [],
      'PassengerInfo.form.visa_country.Text',
    ),
  };
}

const buildVisaSection = (
  apisFields: ApisFields | undefined,
  nationality: string | undefined,
  isEUNational: boolean | undefined,
  euList: string[] | undefined,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
): DynamicFormSection => {
  const visaFieldCodes = [
    ApisFieldCode.VIT,
    ApisFieldCode.VIN,
    ApisFieldCode.VDE,
    ApisFieldCode.VCI,
  ];
  const isVisaInfo = visaFieldCodes.some((code) => isFieldVisible(code, apisFields));
  const shouldShowVisaType = showVisaType(apisFields, nationality, isEUNational, euList);
  if (!isVisaInfo && !shouldShowVisaType) {
    return { id: ApisFormSectionId.VisaInformation, fields: [] };
  }

  const fields: DynamicFieldConfig[] = [];
  const visaTypeField = createVisaTypeField(
    apisFields,
    nationality,
    isEUNational,
    euList,
    passengerIndex,
    onFieldError,
  );
  if (visaTypeField) fields.push(visaTypeField);
  const visaNumberField = createVisaNumberField(apisFields, passengerIndex, onFieldError);
  if (visaNumberField) fields.push(visaNumberField);
  const visaExpiryField = createVisaExpiryField(apisFields, passengerIndex, onFieldError);
  if (visaExpiryField) fields.push(visaExpiryField);
  const countryOfVisaField = createCountryOfVisaField(apisFields, passengerIndex, onFieldError);
  if (countryOfVisaField) fields.push(countryOfVisaField);
  const hasRequiredVisaField = visaFieldCodes.every((code) => isFieldRequired(code, apisFields));

  return fields.length > 0
    ? {
        id: ApisFormSectionId.VisaInformation,
        className: 'passenger-details-visa-information',
        title: getResource('PassengerInfo.form.visa_info.Text'),
        ...(hasRequiredVisaField ? { subtitle: getResource('PassengerInfo.Mandatory.Text') } : {}),
        showDivider: true,
        ...(shouldShowVisaType ? { disclaimer: createVisaTypeDisclaimerNote() } : {}),
        fields,
      }
    : { id: ApisFormSectionId.VisaInformation, fields: [] };
};

const getSkywardDetails = (
  onAddEmiratesSkywardsClick: () => void,
  activePassengerResPaxId?: number,
  skywardNumber?: string,
): unknown => {
  const storedSkywardNumber =
    activePassengerResPaxId !== undefined
      ? selectSkywardNumber(activePassengerResPaxId)(useAppStore.getState())
      : undefined;
  const resolvedSkywardNumber = storedSkywardNumber ?? skywardNumber;
  const hasSkywardNumber = Boolean(resolvedSkywardNumber?.trim());
  const data = {
    id: 'add-emirates-skywards-number',
    showDivider: false,
    fields: [
      {
        key: 'addEmiratesSkywardsNumber',
        type: 'button',
        col: 'col-12',
        text: hasSkywardNumber
          ? `${resolvedSkywardNumber?.trim()}`
          : getResource('SelectPassenger.AddOpenID.Text'),
        onClick: !hasSkywardNumber ? onAddEmiratesSkywardsClick : undefined,
        buttonProps: {
          variant: hasSkywardNumber ? 'solid' : 'link',
          variantColor: 'primary',
          size: 'sm',
          align: 'start',
          fullWidth: false,
          labelProps: {
            className: 'label5',
            fontWeightStyle: hasSkywardNumber ? 'regular' : 'medium',
            variant: hasSkywardNumber ? 'tertiary' : 'accent-accent2',
          },
        },
      },
    ],
  };
  return data;
};

/**
 * Maps NotCheckedInPax and ValidationRules to DynamicFormSection[] config
 * for use with DynamicFormFields component from @fz/ui-components-react
 *
 * @param passenger - Passenger data with apisInfo
 * @param validationRules - Validation rules containing apisFields configuration
 * @returns Array of sections matching DynamicFormFields schema
 */
// eslint-disable-next-line max-lines-per-function
export const mapPassengerToDynamicFormSections = (
  passenger: NotCheckedInPax,
  validationRules?: ValidationRules,
  flightDate?: string,
  formValues?: Record<string, string>,
  passengerIndex?: number,
  onFieldError?: FieldErrorHandler,
  isTablet?: boolean,
  onAddEmiratesSkywardsClick?: () => void,
  activePassengerResPaxId?: number,
): DynamicFormSection[] => {
  const apisFields = validationRules?.apisFields;
  const apisInfo = passenger.apisInfo;
  const selectedNationality = formValues?.[ApisFormFieldKey.Nationality]?.trim();
  const effectiveNationality =
    selectedNationality === ''
      ? apisInfo?.nationality
      : (selectedNationality ?? apisInfo?.nationality);
  const sections: DynamicFormSection[] = [];

  // Helper to safely push a section if valid
  const pushSection = (section: unknown): void => {
    if (
      typeof section === 'object' &&
      section !== null &&
      'fields' in section &&
      Array.isArray((section as { fields: unknown }).fields) &&
      (section as { fields: unknown[] }).fields.length > 0
    ) {
      sections.push(section as DynamicFormSection);
    }
  };
  if (isTablet && passenger.passengerType !== PassengerTypeEnum.Infant) {
    pushSection(getSkywardDetails(() => onAddEmiratesSkywardsClick?.(), activePassengerResPaxId));
  }

  // Section 1: Basic Information
  pushSection(
    buildBasicInfoSection(
      apisFields,
      effectiveNationality,
      passenger.isGCCNational as boolean | null | undefined,
      validationRules?.gccList,
    ),
  );

  // Section 2: Passport Details
  // DocumentSubType may contain passport subtype codes; map all non-23 values to Passport ('1').
  const selectedDocumentType = formValues?.[ApisFormFieldKey.DocumentSubType]?.trim();
  let documentType: string = DocumentType.PASSPORT;
  const hasSelectedDocumentType = selectedDocumentType !== undefined;
  if (
    (hasSelectedDocumentType && selectedDocumentType === DocumentType.NATIONAL_ID) ||
    (!hasSelectedDocumentType && apisInfo?.documentType === DocumentType.NATIONAL_ID)
  ) {
    documentType = DocumentType.NATIONAL_ID;
  }
  pushSection(
    buildPassportDetailsSection(
      apisFields,
      documentType,
      passenger.passengerType,
      flightDate,
      passengerIndex,
      onFieldError,
    ),
  );

  // Section 3: Visa Information
  pushSection(
    buildVisaSection(
      apisFields,
      effectiveNationality,
      passenger.isEUNational,
      validationRules?.euList,
      passengerIndex,
      onFieldError,
    ),
  );

  return sections;
};

export const arePassengerRequiredApisFieldsFilled = (
  passenger: NotCheckedInPax,
  validationRules: ValidationRules | undefined,
  apisValues: Record<string, string>,
  flightDate?: string,
): boolean => {
  if (!validationRules?.apisFields) {
    return false;
  }

  const mergedApisValues = {
    ...mapApisInfoToDynamicFormValues(passenger.apisInfo),
    ...apisValues,
  };

  const sections = mapPassengerToDynamicFormSections(
    passenger,
    validationRules,
    flightDate,
    mergedApisValues,
  );

  for (const section of sections) {
    for (const field of section.fields) {
      if (!('required' in field) || field.required !== true) {
        continue;
      }

      const fieldKey = String(field.key);
      if (!mergedApisValues[fieldKey]?.trim()) {
        return false;
      }
    }
  }

  return true;
};

export const getFieldsToResetOnDocumentSubTypeChange = (
  passenger: NotCheckedInPax,
  validationRules: ValidationRules | undefined,
  flightDate: string | undefined,
  formValues: Record<string, string>,
  previousSubType: string | undefined,
  nextSubType: string,
): string[] => {
  const previous = previousSubType?.trim() ?? '';
  const next = nextSubType.trim();

  if (!previous || !next || previous === next) {
    return [];
  }

  const beforeSections = mapPassengerToDynamicFormSections(passenger, validationRules, flightDate, {
    ...formValues,
    [ApisFormFieldKey.DocumentSubType]: previous,
  });
  const afterSections = mapPassengerToDynamicFormSections(passenger, validationRules, flightDate, {
    ...formValues,
    [ApisFormFieldKey.DocumentSubType]: next,
  });

  const passportDetailsSectionId = ApisFormSectionId.PassportDetails as string;
  const passportBefore =
    beforeSections.find((section) => section.id === passportDetailsSectionId)?.fields ?? [];
  const passportAfter =
    afterSections.find((section) => section.id === passportDetailsSectionId)?.fields ?? [];
  const afterFieldsByKey = new Map(passportAfter.map((field) => [String(field.key), field]));

  return passportBefore
    .filter((field) => {
      const fieldKey = String(field.key);
      const nextField = afterFieldsByKey.get(fieldKey);

      if (!nextField) {
        return true;
      }

      const beforeLabel = 'label' in field ? field.label : undefined;
      const afterLabel = 'label' in nextField ? nextField.label : undefined;

      return beforeLabel !== afterLabel;
    })
    .map((field) => String(field.key));
};

const default_Selected_Value = { code: '', label: '', dialCode: '', value: '' };
export const getSelectedCountry = (dialCode: string): CountryOption | undefined => {
  const countryList = selectCountries(useMasterDataStore.getState());
  const selected = getCountryOptionsWithSelected(countryList, dialCode).selectedOption;
  if (!selected) return default_Selected_Value;
  return {
    code: selected.value,
    label: selected.label,
    dialCode: selected.code,
    value: selected.value,
  };
};
